import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchCurrentFinancialDate } from '../services/financialDateService';
import { useAuth } from './AuthProvider';
import { Schemas } from '../types/api-types';

type FinancialDateResponse = Schemas['FinancialDateResponse'];
type FinancialDateContextType = {
    currentFinancialDate: FinancialDateResponse | null;
    isDateMismatch: boolean;
    loading: boolean;
    setCurrentFinancialDate: (date: FinancialDateResponse) => void;
};

const FinancialDateContext = createContext<FinancialDateContextType | undefined>(undefined);

export const FinancialDateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentFinancialDate, setCurrentFinancialDate] = useState<FinancialDateResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [isDateMismatch, setIsDateMismatch] = useState(false);
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        if (isAuthenticated) {
            setLoading(true);
            fetchDate();
        }
    }, [isAuthenticated]);

    const fetchDate = async () => {
        try {
            const date = await fetchCurrentFinancialDate();
            setCurrentFinancialDate(date);
            setIsDateMismatch(date.business_date.split("T")[0] !== date.server_date.split("T")[0]);
        } catch (error: any) {
            setCurrentFinancialDate(null);
        } finally {
            setLoading(false);
        }

    };

    return (
        <FinancialDateContext.Provider value={{ currentFinancialDate, isDateMismatch, loading, setCurrentFinancialDate }}>
            {children}
        </FinancialDateContext.Provider>
    );
};

export const useFinancialDate = () => {
    const context = useContext(FinancialDateContext);
    if (!context) {
        throw new Error('useFinancialDate must be used within a FinancialDateProvider');
    }
    return context;
};
